import React from 'react';
import clsx from 'clsx';
import ReactModal from 'react-modal';
import { useInView } from 'react-intersection-observer';
import styles from './FilterMenu.module.scss';
import FormCheckbox from '../../../base-components/Checkbox/FormCheckbox.js';
import { Col } from '../components/Col.js';
import { Delimiter } from '../../../base-components/Delimiter.js';
import { FormDateTimePicker } from '../../../base-components/DateTimePicker/FormDateTimePicker.js';
import { FormNumberInput } from '../../../base-components/FormNumberInput.js';
import { ResetButton } from '../components/ResetButton.js';
import { Row } from '../components/Row.js';
import { SearchButton } from '../components/SearchButton.js';
import { FormSelectBlock } from '../components/SelectBlock/index.js';
import { FormSelectOkpd2 } from '../components/SelectOkpd2/index.js';
import { FormSelectProcedureTemplate } from '../components/SelectProcedureTemplate/index.js';
import { FormSelectRegions } from '../components/SelectRegion/FormSelectRegions.js';
import { FormSelectSection } from '../components/SelectSection/index.js';
import { FormSelectTariff } from '../components/SelectTariff/index.js';
import { setEndOfDay } from '../utils.js';
import { setStartOfDay } from '../utils.js';
import type { PurchasesState } from '../Purchases.type.js';
import type { Matches, SelectableOptions } from './Filter.type.js';
import { CancelButton } from '../components/CancelButton.js';

const Period = () => <Delimiter className={styles.period}>-</Delimiter>;
const FilterLabel: React.FC<{ label: string; ref?: unknown }> = ({ label }) => (
  <div className={styles.label}>{label}</div>
);

export type FilterMenuProps = {
  emptyFilterState: PurchasesState;
  matches: Matches;
  onHide: () => void;
  selectableOptions: SelectableOptions;
  showRegionsModal: () => void;
};

export const FilterMenu: React.FC<FilterMenuProps> = ({
  emptyFilterState,
  matches,
  onHide,
  selectableOptions,
  showRegionsModal,
}) => {
  const [modalIsOpen, setIsOpen] = React.useState(true);
  const { ref, inView, entry } = useInView({
    /* Optional options */
    threshold: 0,
  });

  const closeModal = () => {
    setIsOpen(false);
  };
  return (
    <ReactModal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      onAfterClose={onHide}
      closeTimeoutMS={200}
      contentLabel="modal"
      className={styles.modal}
      ariaHideApp={false}
    >
      <Row className={clsx(styles.head, !inView && styles.tinyHead)}>
        <h2>Фильтры</h2>
        <ResetButton
          emptyFilterState={emptyFilterState}
          className={styles.reset}
        />
      </Row>
      <Col className={clsx(styles.filterBody, !inView && styles.tinyHead)}>
        <Col>
          <div ref={ref}></div>
          <FilterLabel label="Регион" />
          <FormSelectRegions
            name="region"
            instanceId="select-region"
            regions={selectableOptions.regions}
            className={clsx(styles.control, styles.region)}
            onPlusClick={showRegionsModal}
            appearance="compact"
            matches={matches?.regions}
          />
        </Col>
        <Col>
          <FilterLabel label="Код ОКПД2" />
          <FormSelectOkpd2
            name="okpd2"
            instanceId="select-okpd2"
            className={clsx(styles.control, styles.okpd2)}
            appearance="compact"
          />
        </Col>
        <Col>
          <FilterLabel label="Секция ЭТП" />
          <FormSelectSection
            name="section"
            instanceId="select-section"
            sections={selectableOptions.sections}
            className={clsx(styles.control, styles.section)}
            appearance="compact"
            matches={matches?.sections}
          />
        </Col>
        <Col>
          <FilterLabel label="Тариф" />
          <FormSelectTariff
            name="tariff"
            instanceId="select-tariff"
            tariffs={selectableOptions.tariffs}
            className={clsx(styles.control, styles.tariff)}
            appearance="compact"
            matches={matches?.tariffs}
          />
        </Col>
        <Col>
          <FilterLabel label="Этап" />
          <FormSelectBlock
            name="block"
            instanceId="select-block"
            blocks={selectableOptions.blocks}
            className={clsx(styles.control)}
            appearance="compact"
            matches={matches?.blocks}
          />
        </Col>
        <Col>
          <FilterLabel label="Дата начала подачи заявок" />
          <Row className={styles.breakpointC}>
            <FormDateTimePicker
              name="receiveStartFrom"
              className={clsx(styles.control, styles.receiveStartFrom)}
              placeholder="Начало подачи заявок с"
              format="date"
              appearance="compact"
              setValue={setStartOfDay}
            />
            <Period />
            <FormDateTimePicker
              name="receiveStartTo"
              className={clsx(styles.control, styles.receiveStartTo)}
              placeholder="по"
              format="date"
              appearance="compact"
              setValue={setEndOfDay}
            />
          </Row>
        </Col>
        <Col>
          <FilterLabel label="Дата окончания подачи заявок" />
          <Row className={styles.breakpointC}>
            <FormDateTimePicker
              name="receiveFinishFrom"
              className={clsx(styles.control, styles.receiveFinishFrom)}
              placeholder="Окончание подачи заявок с"
              format="date"
              appearance="compact"
              setValue={setStartOfDay}
            />
            <Period />
            <FormDateTimePicker
              name="receiveFinishTo"
              className={clsx(styles.control, styles.receiveFinishTo)}
              placeholder="по"
              format="date"
              appearance="compact"
              setValue={setEndOfDay}
            />
          </Row>
        </Col>
        <Col>
          <FilterLabel label="Сумма НМЦ" />
          <Row className={styles.breakpointC}>
            <FormNumberInput
              name="startPriceFrom"
              placeholder="Сумма НМЦ от"
              precision={0}
              className={clsx(styles.control, styles.startPriceFrom)}
              appearance="compact"
            />
            <Period />
            <FormNumberInput
              name="startPriceTo"
              placeholder="до"
              precision={0}
              className={clsx(styles.control, styles.startPriceTo)}
              appearance="compact"
            />
          </Row>
        </Col>
        <Col>
          <FilterLabel label="Способ закупки" />
          <FormSelectProcedureTemplate
            name="procedureTemplate"
            instanceId="select-procedure-template"
            procedureTemplates={selectableOptions.procedureTemplates}
            className={clsx(styles.control, styles.procedureTemplate)}
            appearance="compact"
            matches={matches?.procedureTemplates}
          />
          <Delimiter />
          <div className={clsx(styles.control, styles.freeParticipation)}>
            <FormCheckbox name="freeParticipation" label="Бесплатное участие" />
          </div>
        </Col>
      </Col>
      <Row className={clsx(styles.actions, styles.breakpointC)}>
        <SearchButton className={styles.submit} />
        <Delimiter />
        <CancelButton
          emptyFilterState={emptyFilterState}
          className={styles.reset}
          onCancel={onHide}
        />
      </Row>
    </ReactModal>
  );
};
